import { MDBDataTable } from "mdbreact";
import { useState } from "react";
import { Col, Input, Row, Spinner } from "reactstrap";
import api from "../../services/api";
import "./datatables.scss";
import "./MonthlyWisePaymentsStatistics.css";
import MonthlyEarnings from "./montly-earnings";


/* ---------------- STAT CARD COMPONENT ---------------- */
function StatCard({
  title,
  amount,
  count,
  iconBg,
  iconColor,
  isSelected,
  onClick,
  formatCurrency,
}) {
  return (
    <div
      onClick={onClick}
      className={`summary-card modern-stat-card cursor-pointer ${
        isSelected ? "active" : ""
      }`}
    >
      <div className="summary-card-header d-flex justify-content-between align-items-start">
        <div>
          <h6 className="fw-bold text-dark mb-1">{title}</h6>
          <p className="text-muted small mb-0">Tap to view detailed records</p>
        </div>

        <div className={`summary-icon ${iconBg}`}>
          <i className={`bi bi-bar-chart-fill icon-md ${iconColor}`}></i>
        </div>
      </div>

      <div className="summary-body mt-3">
        <h4 className="fw-bold text-dark mb-1">{formatCurrency(amount)}</h4>
        <p className="small text-muted mb-0">
          {count} {count === 1 ? "record" : "records"}
        </p>
      </div>
    </div>
  );
}

/* ---------------- MAIN COMPONENT ---------------- */

const MonthlyWisePaymentsStatistics = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedView, setSelectedView] = useState(null);

  const handleFetch = async () => {
    if (!year) return;
    setLoading(true);

    try {
      const res = await api.post(
        `${backendUrl}course-payments-overall-by-year-and-month`,
        { year, month }
      );

      if (res.data.success) {
        setData(res.data.data);
        setSelectedView(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  console.log("Fetched Data:", data);

  const formatCurrency = (num) =>
    num?.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    });

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  return (
    <div className="overview-container fade-in">
      <h2 className="fw-bold text-dark mb-1">Month wise Payments</h2>

      <MonthlyEarnings />

      {/* FILTERS */}
      <Row className="g-3 mb-5 mt-2 align-items-center">
        <Col md={3}>
          <label className="fw-semibold text-muted">Select Year</label>
          <Input
            type="select"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="glass-input"
          >
            <option value="">-- Select Year --</option>
            {[2030, 2029, 2028, 2027, 2026, 2025, 2024].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Input>
        </Col>

        <Col md={3}>
          <label className="fw-semibold text-muted">Select Month</label>
          <Input
            type="select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="glass-input"
          >
            <option value="">-- Select Month --</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString("en-US", {
                  month: "long",
                })}
              </option>
            ))}
          </Input>
        </Col>

        <Col md={1}>
          <button
            onClick={handleFetch}
            className="btn btn-primary fw-semibold mt-3 w-100"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Get"}
          </button>
        </Col>
      </Row>

      {/* SUMMARY CARDS */}
      {data && (
        <Row className="g-4 mb-5">
          <Col md={4}>
            <StatCard
              title="Total Payments"
              amount={data.summary.total_payments_amount}
              count={data.summary.total_payments_count}
              iconBg="bg-success-subtle"
              iconColor="text-success"
              isSelected={false}
              onClick={() => setSelectedView("payments")}
              formatCurrency={formatCurrency}
            />
          </Col>

          <Col md={4}>
            <StatCard
              title="Total Refunds"
              amount={data.summary.total_refunds_amount}
              count={data.summary.total_refunds_count}
              iconBg="bg-danger-subtle"
              iconColor="text-danger"
              isSelected={false}
              onClick={() => setSelectedView("refunds")}
              formatCurrency={formatCurrency}
            />
          </Col>

          <Col md={4}>
            <StatCard
              title="Net in Cash Counter"
              amount={data.summary.net_amount_in_cashcounter}
              count={
                data.summary.total_payments_count +
                data.summary.total_refunds_count
              }
              iconBg="bg-primary-subtle"
              iconColor="text-primary"
              isSelected={false}
              onClick={() => setSelectedView("net")}
              formatCurrency={formatCurrency}
            />
          </Col>
        </Row>
      )}

      {/* ---------------- PAYMENTS TABLE (MDB) ---------------- */}
      {selectedView === "payments" && (
        <>
          <h4 className="fw-bold text-primary mb-3">Payments</h4>

          <div className="datatable-container fade-in">
            <MDBDataTable
              striped
              bordered
              hover
              responsive
              entries={10}
              pagesAmount={5}
              theadColor="light"
              className="custom-mdb-table"
              data={{
                columns: [
                  { label: "S.no", field: "sno", sort: "asc" },
                  { label: "Roll No", field: "roll_no", sort: "asc" },
                  { label: "Name", field: "name", sort: "asc" },
                  { label: "Amount", field: "amount", sort: "asc" },
                  { label: "Term", field: "term", sort: "asc" },
                  { label: "Payment ID", field: "payment_id", sort: "asc" },
                  { label: "College", field: "college", sort: "asc" },
                  { label: "Branch", field: "branch", sort: "asc" },
                  { label: "Course", field: "course", sort: "asc" },
                  { label: "Technology", field: "tech", sort: "asc" },
                  { label: "Cashier", field: "cashier", sort: "asc" },
                  { label: "Date", field: "date", sort: "asc" },
                ],
                rows: data.all_payments.map((p, idx) => ({
                  sno: idx + 1,
                  roll_no: p.roll_no,
                  name: p.name,
                  amount: (
                    <span className="text-success fw-semibold">
                      {formatCurrency(p.amount)}
                    </span>
                  ),
                  term: p.term_name,
                  payment_id: (
                    <span className="text-muted small">{p.payment_id}</span>
                  ),
                  college: p.college,
                  branch: Array.isArray(p.branch)
                    ? p.branch.join(", ")
                    : p.branch,
                  course: p.course_name,
                  tech: p.technology_name,
                  cashier: p.cashier_name,
                  date: formatDate(p.payment_date),
                })),
              }}
            />
          </div>
        </>
      )}

      {/* ---------------- REFUNDS TABLE (MDB) ---------------- */}
      {selectedView === "refunds" && (
        <>
          <h4 className="fw-bold text-danger mb-3">Refunds</h4>

          <div className="datatable-container fade-in">
            <MDBDataTable
              striped
              bordered
              hover
              responsive
              entries={10}
              pagesAmount={5}
              theadColor="light"
              className="custom-mdb-table"
              data={{
                columns: [
                  { label: "S.no", field: "sno", sort: "asc" },
                  { label: "Roll No", field: "roll_no", sort: "asc" },
                  { label: "Name", field: "name", sort: "asc" },
                  { label: "Amount", field: "amount", sort: "asc" },
                  { label: "Reason", field: "reason", sort: "asc" },
                  { label: "Term", field: "term", sort: "asc" },
                  { label: "Payment ID", field: "payment_id", sort: "asc" },
                  { label: "Refund Date", field: "refund_date", sort: "asc" },
                  { label: "College", field: "college", sort: "asc" },
                  { label: "Cashier", field: "cashier", sort: "asc" },
                ],
                rows: data.refunds_given.map((r, idx) => ({
                  sno: idx + 1,
                  roll_no: r.roll_no,
                  name: r.name,
                  amount: (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(r.amount)}
                    </span>
                  ),
                  reason: r.refund_reason,
                  term: r.term_name,
                  payment_id: (
                    <span className="text-muted small">{r.payment_id}</span>
                  ),
                  refund_date: formatDate(r.refund_date),
                  college: r.college,
                  cashier: r.cashier_name,
                })),
              }}
            />
          </div>
        </>
      )}

      {/* NET SUMMARY */}
      {selectedView === "net" && (
        <div className="text-center p-5 border-dashed rounded-3 text-muted">
          <h5>Net Amount in Cash Counter</h5>
          <h2 className="fw-bold text-primary mt-2">
            {formatCurrency(data.summary.net_amount_in_cashcounter)}
          </h2>
          <p className="mt-2 small">
            Based on {data.summary.total_payments_count} payments and{" "}
            {data.summary.total_refunds_count} refunds.
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyWisePaymentsStatistics;
